import { type FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bug,
  CheckCircle2,
  FlaskConical,
  HelpCircle,
  Lightbulb,
  type LucideIcon,
  MessageCircle,
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import type { FeedbackArea, FeedbackCategory, FeedbackSeverity } from '@lizrd/contracts';
import { countWords, FEEDBACK_MESSAGE_MAX_WORDS } from '@lizrd/contracts';
import { submitFeedback } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { FAQ_CATEGORIES } from './help/faqs';

type Tab = 'help' | 'contact';

const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string; icon: LucideIcon }[] = [
  { value: 'problem', label: 'Problem', icon: Bug },
  { value: 'idea', label: 'Idea', icon: Lightbulb },
  { value: 'question', label: 'Question', icon: HelpCircle },
  { value: 'general', label: 'General', icon: MessageCircle },
];
const AREA_OPTIONS: { value: FeedbackArea; label: string }[] = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'recommendations', label: 'Recommendations' },
  { value: 'domains', label: 'Domains' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'members', label: 'Members & access' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Something else' },
];
const SEVERITY_OPTIONS: { value: FeedbackSeverity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'blocking', label: 'Blocking' },
];

/** The searchable FAQ list with a category jump-nav. */
function Faqs() {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const categories = useMemo(() => {
    if (!q) return FAQ_CATEGORIES;
    return FAQ_CATEGORIES.map((c) => ({
      ...c,
      items: c.items.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
      ),
    })).filter((c) => c.items.length > 0);
  }, [q]);

  return (
    <div className="space-y-4">
      <Input
        type="search"
        placeholder="Search help…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search help"
      />
      {!q && (
        <div className="flex flex-wrap gap-2">
          {FAQ_CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#faq-${c.id}`}
              className="rounded-full border border-hairline bg-surface-2 px-3 py-1 text-xs text-haze transition-colors hover:text-bone"
            >
              <c.icon aria-hidden className="inline-block h-3.5 w-3.5 align-text-bottom" />{' '}
              {c.title}
            </a>
          ))}
        </div>
      )}
      {categories.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted">
          No help articles match “{query}”. Try the Contact tab to reach us.
        </p>
      ) : (
        categories.map((c) => (
          <Card key={c.id} id={`faq-${c.id}`} className="scroll-mt-24 p-5">
            <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-haze">
              <c.icon aria-hidden className="h-4 w-4" />
              {c.title}
            </h2>
            <div className="divide-y divide-hairline">
              {c.items.map((it) => (
                <details key={it.q} className="group py-2">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-bone">
                    {it.q}
                    <span
                      className="text-muted transition-transform group-open:rotate-90"
                      aria-hidden
                    >
                      ›
                    </span>
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-haze">
                    {it.a}
                    {it.to && (
                      <>
                        {' '}
                        <Link to={it.to} className="font-semibold text-iris hover:text-iris-strong">
                          Go there →
                        </Link>
                      </>
                    )}
                  </p>
                </details>
              ))}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

/** The structured feedback / support form (disabled in the sandbox demo). */
function ContactForm() {
  const user = useAuthStore((s) => s.user);
  const isSandbox = user?.organizationKind === 'SANDBOX';

  const [category, setCategory] = useState<FeedbackCategory>('problem');
  const [area, setArea] = useState<FeedbackArea>('other');
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [followUp, setFollowUp] = useState(true);

  const wordCount = countWords(message);
  const overLimit = wordCount > FEEDBACK_MESSAGE_MAX_WORDS;

  const send = useMutation({
    mutationFn: () =>
      submitFeedback({
        category,
        area,
        severity: category === 'problem' ? severity : null,
        subject: subject.trim(),
        message: message.trim(),
        followUp,
        context: {
          path: window.location.pathname,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
      }),
  });

  if (isSandbox) {
    return (
      <Card className="space-y-3 p-6 text-center">
        <FlaskConical aria-hidden className="mx-auto h-8 w-8 text-iris" />
        <h2 className="font-display text-lg font-bold text-bone">You're in the sandbox</h2>
        <p className="mx-auto max-w-md text-sm text-muted">
          Feedback and support aren't available in the demo. Create your own workspace to connect
          your cloud and reach our team — the FAQs are here to help in the meantime.
        </p>
        <Link
          to="/register"
          className="inline-block rounded-sm bg-iris px-4 py-2 text-sm font-semibold text-on-iris"
        >
          Create your workspace
        </Link>
      </Card>
    );
  }

  if (send.isSuccess) {
    return (
      <Card className="space-y-3 p-6 text-center">
        <CheckCircle2 aria-hidden className="mx-auto h-8 w-8 text-viridian" />
        <h2 className="font-display text-lg font-bold text-bone">Thanks — we've got it</h2>
        <p className="text-sm text-muted">
          Reference <span className="font-mono text-bone">#{send.data.reference}</span>.
          {followUp && ` We'll follow up at ${user?.email} if we need more.`}
        </p>
        <Button variant="outline" size="sm" onClick={() => send.reset()}>
          Send another
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (subject.trim() && message.trim() && !overLimit) send.mutate();
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
            What's this about?
          </label>
          <div className="mt-1.5 inline-flex flex-wrap gap-1 rounded-sm border border-border p-1">
            {CATEGORY_OPTIONS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCategory(c.value)}
                className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm transition-colors ${
                  category === c.value ? 'bg-iris text-on-iris' : 'text-haze hover:text-bone'
                }`}
              >
                <c.icon aria-hidden className="h-4 w-4" />
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
              Area
            </label>
            <div className="mt-1.5">
              <Select value={area} onChange={setArea} options={AREA_OPTIONS} ariaLabel="Area" />
            </div>
          </div>
          {category === 'problem' && (
            <div>
              <label className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                Severity
              </label>
              <div className="mt-1.5">
                <Select
                  value={severity}
                  onChange={setSeverity}
                  options={SEVERITY_OPTIONS}
                  ariaLabel="Severity"
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
            Subject
          </label>
          <Input
            className="mt-1.5"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="A one-line summary"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
            Details
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What happened, what you expected, and anything that helps us reproduce it…"
            rows={5}
            maxLength={5000}
            required
            aria-invalid={overLimit}
            className={`mt-1.5 w-full resize-y rounded-md border bg-surface-2 px-3 py-2 text-sm text-bone placeholder:text-muted focus:outline-none ${
              overLimit ? 'border-danger focus:border-danger' : 'border-border focus:border-iris'
            }`}
          />
          <div
            className={`mt-1 text-right text-[0.7rem] ${overLimit ? 'text-danger' : 'text-muted'}`}
          >
            {wordCount} / {FEEDBACK_MESSAGE_MAX_WORDS} words
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-haze">
          <input
            type="checkbox"
            checked={followUp}
            onChange={(e) => setFollowUp(e.target.checked)}
            className="h-4 w-4 accent-iris"
          />
          OK to follow up with me at <span className="font-medium text-bone">{user?.email}</span>
        </label>

        {send.isError && (
          <p className="text-sm text-danger">
            {send.error instanceof Error ? send.error.message : 'Could not send — please retry.'}
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={!subject.trim() || !message.trim() || overLimit || send.isPending}
          >
            {send.isPending ? <Spinner size="sm" /> : 'Send'}
          </Button>
          <span className="text-[0.7rem] text-muted">
            We'll attach your account, role, and current page for context.
          </span>
        </div>
      </form>
    </Card>
  );
}

/**
 * Help & Feedback — a top-level page for every role. FAQs (always available) plus
 * a structured feedback/support form (disabled in the sandbox demo).
 */
export function HelpPage() {
  const [tab, setTab] = useState<Tab>('help');
  const tabClass = (active: boolean) =>
    `border-b-2 px-3 py-2 text-sm transition-colors ${
      active ? 'border-iris text-bone' : 'border-transparent text-haze hover:text-bone'
    }`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Help &amp; Feedback</h1>
        <p className="text-sm text-muted">
          Find answers fast, or tell us what would make Lizrd better.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-hairline">
        <button type="button" onClick={() => setTab('help')} className={tabClass(tab === 'help')}>
          Help &amp; FAQs
        </button>
        <button
          type="button"
          onClick={() => setTab('contact')}
          className={tabClass(tab === 'contact')}
        >
          Contact &amp; feedback
        </button>
      </div>

      {tab === 'help' ? <Faqs /> : <ContactForm />}
    </div>
  );
}
